import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Schedule,
  Save,
  Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const MarkAttendance = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchClasses = useCallback(async () => {
    try {
      const response = await teacherAPI.getClasses();
      setClasses(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      showSnackbar('Error fetching classes', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getStudentsForAttendance(selectedClass, selectedDate);
      const studentsData = response.data.students || [];
      
      setStudents(studentsData);
      
      // Initialize attendance state
      const initialAttendance = {};
      studentsData.forEach(student => {
        initialAttendance[student.id] = student.status || 'present';
      });
      setAttendance(initialAttendance);
    } catch (error) {
      console.error('Error fetching students:', error);
      showSnackbar('Error fetching students', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedDate]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) {
      fetchStudents();
    }
  }, [selectedClass, selectedDate, fetchStudents]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleMarkAllPresent = () => {
    const allPresent = {};
    students.forEach(student => {
      allPresent[student.id] = 'present';
    });
    setAttendance(allPresent);
  };

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);
      
      const attendanceData = students.map(student => ({
        student_id: student.id,
        status: attendance[student.id] || 'present'
      }));

      await teacherAPI.markAttendance({
        classId: selectedClass,
        date: selectedDate,
        attendance: attendanceData
      });

      showSnackbar('Attendance marked successfully!', 'success');
    } catch (error) {
      console.error('Error saving attendance:', error);
      showSnackbar('Error saving attendance', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStats = () => {
    const stats = {
      total: students.length,
      present: 0,
      absent: 0
    };

    Object.values(attendance).forEach(status => {
      if (status === 'present') stats.present++;
      else if (status === 'absent') stats.absent++;
    });

    stats.percentage = stats.total > 0 ? 
      Math.round((stats.present / stats.total) * 100) : 0;

    return stats;
  };

  const stats = getStats();

  if (loading && classes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Mark Attendance
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/teacher/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Selection Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Select Class</InputLabel>
              <Select
                value={selectedClass}
                label="Select Class"
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name} - Grade {cls.grade} {cls.section}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              type="date"
              label="Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={handleMarkAllPresent}
              disabled={!selectedClass || students.length === 0}
            >
              Mark All Present
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {selectedClass && students.length > 0 && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Total Students
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.total}
                      </Typography>
                    </Box>
                    <Person sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Present
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {stats.present}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Absent
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="error.main">
                        {stats.absent}
                      </Typography>
                    </Box>
                    <Cancel sx={{ fontSize: 40, color: 'error.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Attendance %
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {stats.percentage}%
                      </Typography>
                    </Box>
                    <Schedule sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Students List */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Students List
            </Typography>
            <List>
              {students.map((student, index) => (
                <ListItem
                  key={student.id}
                  sx={{
                    borderBottom: index !== students.length - 1 ? '1px solid #eee' : 'none',
                    py: 2
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight="medium" noWrap>
                        {student.name || student.admission_number || 'Unknown Student'}
                      </Typography>
                    }
                    secondary={`Admission No: ${student.admission_number}`}
                    sx={{ mr: 1, minWidth: 0 }}
                  />
                  <ToggleButtonGroup
                    value={attendance[student.id]}
                    exclusive
                    onChange={(e, value) => value && handleAttendanceChange(student.id, value)}
                    size="small"
                  >
                    <ToggleButton
                      value="present"
                      sx={{
                        minHeight: 48,
                        minWidth: 48,
                        '&.Mui-selected': {
                          backgroundColor: '#4caf50',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#45a049'
                          }
                        }
                      }}
                    >
                      <CheckCircle sx={{ mr: { xs: 0, sm: 0.5 } }} fontSize="small" />
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Present</Box>
                    </ToggleButton>
                    <ToggleButton
                      value="absent"
                      sx={{
                        minHeight: 48,
                        minWidth: 48,
                        '&.Mui-selected': {
                          backgroundColor: '#f44336',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#da190b'
                          }
                        }
                      }}
                    >
                      <Cancel sx={{ mr: { xs: 0, sm: 0.5 } }} fontSize="small" />
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Absent</Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </ListItem>
              ))}
            </List>

            <Box mt={3} mb={{ xs: 8, md: 0 }} display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSaveAttendance}
                disabled={saving}
                sx={{ px: 6, maxWidth: { xs: '100%', sm: 320 } }}
              >
                {saving ? 'Saving...' : 'Save Attendance'}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      {selectedClass && students.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No students found in this class
          </Typography>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MarkAttendance;
